import { Dialog, DialogTitle, DialogContent, DialogActions, Alert, Box } from '@mui/material';
import { TextBlock } from '../../Atoms/Text';
import { CopyButton } from '../CopyButton/CopyButton';
import { DEFAULT_ADVANCE_BUTTON_STYLE } from '../../../utils/styleHelpers';
import { buildClaudeMcpAddCommand } from '../../../utils/mcpAccess';
import { McpAccessProfileWithToken } from '../../../store/McpAccess';

interface McpAccessTokenRevealModalProps {
  open: boolean;
  profile: McpAccessProfileWithToken | null;
  warning?: string | null;
  onClose: () => void;
}

const monospaceBox = {
  fontFamily: 'monospace',
  fontSize: '0.85rem',
  bgcolor: 'grey.100',
  borderRadius: 1,
  p: 1.5,
  flexGrow: 1,
  wordBreak: 'break-all' as const,
  userSelect: 'all' as const
};

export const McpAccessTokenRevealModal = ({ open, profile, warning, onClose }: McpAccessTokenRevealModalProps) => {
  if (!profile) return null;

  const command = buildClaudeMcpAddCommand({ accessId: profile.id, token: profile.token, scope: profile.scope });

  return (
    <Dialog
      open={open}
      // Backdrop/Escape are ignored so the one-time token cannot be dismissed by accident; only Done closes.
      onClose={(_event, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') onClose();
      }}
      maxWidth="sm"
      fullWidth
      aria-labelledby="mcp-token-reveal-title">
      <DialogTitle id="mcp-token-reveal-title">Copy your access token</DialogTitle>
      <DialogContent>
        {warning && (
          <Alert
            severity="warning"
            data-testid="mcp-token-warning"
            sx={{ mb: 2 }}>
            {warning}
          </Alert>
        )}
        <Alert
          severity="warning"
          sx={{ mb: 2 }}>
          You won&apos;t be able to see this token again. Copy it now and store it somewhere safe.
        </Alert>

        <TextBlock
          weight="semibold"
          size="sm"
          color="grey8">
          Token
        </TextBlock>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 0.5, mb: 2 }}>
          <Box
            component="code"
            data-testid="mcp-token-value"
            data-dd-privacy="mask"
            data-dd-action-name="MCP token"
            sx={monospaceBox}>
            {profile.token}
          </Box>
          <CopyButton
            value={profile.token}
            ariaLabel="Copy token"
            dataTestId="copy-token"
          />
        </Box>

        <TextBlock
          weight="semibold"
          size="sm"
          color="grey8">
          Add to Claude Code
        </TextBlock>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 0.5 }}>
          <Box
            component="code"
            data-testid="mcp-add-command"
            data-dd-privacy="mask"
            data-dd-action-name="MCP add command"
            sx={monospaceBox}>
            {command}
          </Box>
          <CopyButton
            value={command}
            ariaLabel="Copy command"
            dataTestId="copy-command"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <button
          type="button"
          className={DEFAULT_ADVANCE_BUTTON_STYLE}
          onClick={onClose}>
          Done
        </button>
      </DialogActions>
    </Dialog>
  );
};
